import { Typography } from '@atlas-metrics/design-system';
import {
  AggregatedValue,
  MicrositeWidgetEntity,
  WidgetsByStandard,
  WidgetType,
} from '@atlas-metrics/microsite-types/dist/types/typeIndex';
import { useTranslation } from 'next-i18next';
import { MutableRefObject } from 'react';
import { InView } from 'react-intersection-observer';

import formatValue from '@/hooks/useFormatValue';

import { safeLocaleCompare } from '@/utils/safeLocaleCompare';

import ElevatedCard from './ui/ElevatedCard';
import WidgetRow from './ui/WidgetRow';
import WidgetsCard from './ui/WidgetsCard';
import PieChartWidget from './widgets/PieChartWidget';
import ProgressChartWidget from './widgets/ProgressChartWidget';
import StackWidget from './widgets/StackWidget';
import {
  getProgressChartWidgetContent,
  getRowContent,
  hasCategoryStack,
} from './widgets/utils';

const StandardContent = ({
  standard,
  themeRef,
  onThemeViewChange,
  themeNodeRef,
  fontColor,
}: {
  standard: WidgetsByStandard;
  themeRef: (node?: Element | null | undefined) => void;
  onThemeViewChange: (themeId: string, inView: boolean) => void;
  themeNodeRef: MutableRefObject<{
    [key: string]: HTMLElement;
  }>;
  fontColor: string;
}) => {
  const { t, i18n } = useTranslation();
  const accordionText = {
    toOpenText: t('accordion.expand'),
    toCloseText: t('accordion.collapse'),
  };
  const getWidgetContent = (widgets: MicrositeWidgetEntity[]): JSX.Element[] =>
    widgets.map((widget, widgetIdx) => {
      if (widget.type === WidgetType.PIE_CHART) {
        return (
          <PieChartWidget
            key={widgetIdx}
            disclosureTitle={widget.translations.title}
            title='Total'
            result={100}
            unit={widget.translations.unitName}
            dataType={widget.unitDataType}
            data={widget.aggregatedData}
            formatValue={(value, dataType) =>
              formatValue(value, dataType, t, i18n.language)
            }
          />
        );
      }

      if (widget.type === WidgetType.PROGRESS_CHART) {
        return (
          <ProgressChartWidget
            key={widgetIdx}
            formatValue={(value, dataType) =>
              formatValue(value, dataType, t, i18n.language)
            }
            disclosureTitle={widget.translations.title}
            dataType={widget.unitDataType}
            data={getProgressChartWidgetContent(
              widget.translations.title,
              widget.aggregatedData
            )}
          />
        );
      }

      const aggregatedData = widget?.aggregatedData;
      if (hasCategoryStack(aggregatedData)) {
        return (
          <ElevatedCard key={widgetIdx}>
            <WidgetRow
              title={widget.translations.title}
              hideHorizontalDivider
            />
            {aggregatedData.options.map((option, idx) => (
              <StackWidget
                key={idx}
                option={option}
                widget={widget}
                nodeKey={widgetIdx.toString()}
                formatValue={(value, dataType) =>
                  formatValue(value, dataType, t, i18n.language)
                }
              />
            ))}
          </ElevatedCard>
        );
      }

      return (
        <ElevatedCard key={widgetIdx}>
          <WidgetRow
            title={widget.translations.title}
            {...getRowContent(
              widget,
              widget?.aggregatedData as AggregatedValue,
              (value, dataType) =>
                formatValue(value, dataType, t, i18n.language),
              t
            )}
            accordion={accordionText}
          />
        </ElevatedCard>
      );
    });

  function getWidgetsGroupsByNetwork(widgets: MicrositeWidgetEntity[]) {
    const sortedWidgets = [...widgets].sort(
      (a, b) =>
        // sort by network name and then by network id; we rely on order of network id to group the widgets
        safeLocaleCompare(
          a.translations.networkName,
          b.translations.networkName
        ) || a.networkID.localeCompare(b.networkID)
    );

    return sortedWidgets.reduce<MicrositeWidgetEntity[][]>(
      (groupedWidgets, widget) => {
        const lastIndex = groupedWidgets.length - 1;
        const lastGroup: MicrositeWidgetEntity[] = groupedWidgets[lastIndex];
        const hasSameNetworkID = lastGroup?.some(
          (w) => w.networkID === widget.networkID
        );
        if (hasSameNetworkID) {
          const allGroupsExceptLast = groupedWidgets.slice(0, lastIndex);
          return [...allGroupsExceptLast, [...lastGroup, widget]];
        } else {
          return [...groupedWidgets, [widget]];
        }
      },
      []
    );
  }

  return (
    <>
      <div className='flex w-full scroll-mt-5 px-6 py-4' id={standard.id}>
        <Typography variant='h6'>{standard.name}</Typography>
      </div>

      {standard.categories.map((category) =>
        category.themes.map((theme) => (
          <div key={theme.id} ref={themeRef}>
            <div
              ref={(ref) => {
                if (ref) {
                  themeNodeRef.current = {
                    ...themeNodeRef.current,
                    [theme.id]: ref,
                  };
                }
              }}
              className='mb-2 flex w-full flex-wrap gap-2 px-6 py-4'
            >
              <div style={{ color: fontColor }}>
                <Typography variant='h6'>{category.name}</Typography>
              </div>
              <Typography variant='h6'>{theme.name}</Typography>
            </div>
            <InView
              as='div'
              onChange={(inView) => onThemeViewChange(theme.id, inView)}
            >
              {theme.subThemes.map((subTheme) => {
                return getWidgetsGroupsByNetwork(subTheme.widgets).map(
                  (widgetsGroup) => (
                    <div key={widgetsGroup[0].networkID}>
                      <WidgetsCard
                        title={subTheme.name}
                        className='mb-6'
                        key={widgetsGroup[0].networkID}
                        network={widgetsGroup[0]?.translations.networkName}
                      >
                        {getWidgetContent(widgetsGroup)}
                      </WidgetsCard>
                    </div>
                  )
                );
              })}
            </InView>
          </div>
        ))
      )}
    </>
  );
};

export default StandardContent;
